import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDrawer } from '@angular/material/sidenav';
import { ActivatedRoute, NavigationEnd, Params } from '@angular/router';
import { DrawerService } from '@app/shared/services/drawer.service';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { HotkeysService } from '@ngneat/hotkeys';

import { debounceTime, distinctUntilChanged, filter, merge, Observable, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { BaseComponent } from '../base/base.component';
import { ViewType } from './models/common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ServiceReadyStateService } from '@app/core/services/service-ready-state.service';
@UntilDestroy()
@Component({
    selector: 'app-list-page-base',
    templateUrl: './list-page-base.component.html',
    styleUrls: ['./list-page-base.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        "style": "width: inherit; height: inherit;",
        "class": "smooth-transition"
    },
    standalone: false
})
export class ListPageBaseComponent extends BaseComponent implements OnInit, OnDestroy {

    protected pageTitle = 'SystemAdmin.PageTitles.Dashboard';

    @ViewChild('matDrawer', { static: true }) matDrawer: MatDrawer;

    @Input()
    fixedHeader: boolean = true;

    @Input()
    customHeader: boolean = false;

    @Input()
    customHeaderAction: boolean = false;

    @Input()
    public title: string;

    @Input()
    public description: string;

    @Input()
    public icon: IconProp = ['fal', 'layer-group'];

    @Input()
    disableAdd: boolean = false;

    @Input()
    hideAdd: boolean = false;

    @Input()
    addButtonMenu: boolean = false;

    public _searchTerm: string;


    @Input()
    public get searchTerm(): string {
        return this._searchTerm;
    }

    public set searchTerm(val: string) {
        this._searchTerm = val;
    }

    @Output()
    public searchTermChange: EventEmitter<string> = new EventEmitter<string>();;

    @Output()
    public newItemClick: EventEmitter<void> = new EventEmitter();

    @Output()
    public resolvedId: EventEmitter<string> = new EventEmitter();

    public searchControl: FormControl;
    private searchDebounce: number = 1000;

    public _supportedViews: ViewType = ViewType.table | ViewType.card | ViewType.history | ViewType.settings;

    @Input()
    public get supportedViews(): ViewType {
        return this._supportedViews;
    }

    public set supportedViews(val: ViewType) {
        this._supportedViews = val;
        this.supportedViewCount = this.getViewCount();
    }

    supportedViewCount: number;

    public _currentView: ViewType = ViewType.table;

    @Input()
    public get currentView(): ViewType { return this._currentView; }
    public set currentView(val: ViewType) {
        this._currentView = val;
        this.currentViewChange.next(val);
    }

    @Output()
    public currentViewChange: EventEmitter<ViewType> = new EventEmitter();

    public viewTypes = ViewType;

    drawerMode: 'side' | 'over';

    private subscriptions: Subject<any> = new Subject<any>();

    constructor(
        private mediaWatcher: FuseMediaWatcherService,
        private activatedRoute: ActivatedRoute,
        private cd: ChangeDetectorRef,
        private hotkeys: HotkeysService,
        private drawerService: DrawerService<any>,
        private readyStateService: ServiceReadyStateService
    ) {
        super();

        this.supportedViews = ViewType.table | ViewType.card | ViewType.history | ViewType.settings;

        this.hotkeys.addShortcut({ keys: 'esc' }).subscribe((e) => {
            if (this.drawerService.isOpen) {
                this.drawerService.close();
            }
        });


        this.searchControl = new FormControl('');
        this.searchControl.valueChanges
            .pipe(debounceTime(this.searchDebounce), distinctUntilChanged(), untilDestroyed(this))
            .subscribe((text) => {
                this.searchTermChange.next(text);
            });

        // Subscribe to media changes
        this.mediaWatcher.onMediaChange$
            .pipe(takeUntil(this.subscriptions))
            .subscribe(({ matchingAliases }) => {

                // Set the drawerMode if the given breakpoint is active
                if (matchingAliases.includes('lg')) {
                    this.drawerMode = 'side';
                }
                else {
                    this.drawerMode = 'over';
                }

                // Mark for check
                this.cd.markForCheck();
            });


        this.readyStateService.allServicesReady$
            .pipe(untilDestroyed(this))
            .subscribe(ready => {
                if (ready) {
                    this.resolveNavigation();
                }
            });


    }


    ngOnInit(): void {
        super.ngOnInit();
        this.log.info(`ListPageBaseComponent created with id: ${this.id}`);

        this.drawerService.openRequested
            .pipe(untilDestroyed(this))
            .subscribe((open) => {
                if (open) {
                    this.matDrawer.open();
                    this.drawerService.isOpen = true;
                }
                else {
                    this.matDrawer.close();
                    this.drawerService.isOpen = false;
                }
            });
    }

    changeView(view: ViewType): void {
        this.currentView = view;
    }

    isViewSupported(view: ViewType): boolean {
        return view === (view & this.supportedViews);
    }

    private resolveNavigation(): void {
        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                startWith(null), // İlk yüklemeyi ele almak için
                untilDestroyed(this),
                switchMap(() => this.getAllRouteParams(this.activatedRoute)),
                debounceTime(100) // 100 msaniye içinde gelen son değişikliği alır
            )
            .subscribe(params => {
                const id = params['id'];

                this.resolvedId.emit(id);

            });
    }

    private getAllRouteParams(route: ActivatedRoute): Observable<Params> {
        const paramsObservables: Observable<Params>[] = [route.params];

        route.children.forEach(childRoute => {
            paramsObservables.push(this.getAllRouteParams(childRoute));
        });

        return merge(...paramsObservables);
    }

    getViewCount(): number {

        let perm: number = 0;
        for (const log in ViewType) {
            if (!isNaN(Number(log)) && this.isViewSupported(Number(log))) {
                perm++;
            }
        }
        return perm;
    }

    ngOnDestroy(): void {
        this.drawerService.close();
        // Unsubscribe from all subscriptions
        this.subscriptions.next(null);
        this.subscriptions.complete();

        this.hotkeys.removeShortcuts('esc');
    }
}
